/**
 * Hidden landing page for readers of "Soaring With the Soul" who type
 * /soarwithme into their browser after reading the URL in the back of
 * Holly's book. NOT linked from anywhere in the site, NOT in the sitemap,
 * NOT indexable.
 *
 * Purpose: capture book-buyer contact info (Amazon doesn't give Holly a
 * customer list) and gift them a free aerial-yoga practice video, a
 * meditation, and a personal message of gratitude.
 */
import React, { useState } from "react";
import { Sparkles, BookOpen, Heart, Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Mandala from "@/components/Mandala";
import SEO from "@/components/SEO";

// Holly drops the Vimeo embed URL here when her gift video is ready.
// Example: "https://player.vimeo.com/video/123456789"
const VIMEO_URL = "";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/49qd9zce_AGAPE-26.jpg";

export default function SoarWithMe() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !name) {
      toast.error("Please share your name and email.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/newsletter", {
        email,
        name,
        source: "soarwithme_book",
        interests: ["book_reader"],
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err) || "Something went sideways. Try again?");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-aviana-paper min-h-screen" data-testid="soarwithme-page">
      {/* Hidden from Google, social crawlers, and the sitemap.
          Holly references this URL only in the back of her book. */}
      <SEO
        noindex
        title="A gift for Soaring With the Soul readers"
        description="A private welcome for readers of Holly Johnson's book Soaring With the Soul."
        path="/soarwithme"
      />

      {/* ───────── HERO ───────── */}
      <section className="relative pt-36 pb-20 md:pt-44 md:pb-28 bg-aviana-plumDeep text-aviana-cream overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover object-center opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-aviana-plumDeep/85 via-aviana-plumDeep/75 to-aviana-plumDeep" />
        <div className="absolute inset-0 grain pointer-events-none opacity-50" aria-hidden />
        <Mandala size={620} variant="full" className="hidden md:block absolute -right-32 -top-24 text-aviana-gold opacity-[0.14] animate-spin-slow" />

        <div className="container-aviana relative z-10 max-w-3xl text-center">
          <div className="inline-flex items-center gap-3 mb-7">
            <Mandala size={24} variant="lotus" className="text-aviana-goldSoft" />
            <p className="overline text-aviana-goldSoft">A gift for my readers</p>
            <Mandala size={24} variant="lotus" className="text-aviana-goldSoft" />
          </div>
          <h1 className="font-display text-[44px] sm:text-[64px] lg:text-[84px] leading-[0.95]" data-testid="soarwithme-headline">
            You found your <em className="italic font-serif gold-text">wings.</em>
          </h1>
          <p className="mt-9 font-serif italic text-[19px] md:text-[23px] leading-[1.55] text-aviana-cream/90 max-w-2xl mx-auto">
            Welcome, sister. The fact that you're here — typing the secret address tucked in the back of <em>Soaring With the Soul</em> — means
            something in those pages stirred you.
          </p>
          <p className="mt-6 text-[14px] md:text-[15px] tracking-[0.04em] text-aviana-cream/75 max-w-xl mx-auto leading-[1.85]">
            And that means more to me than I can put into ink.
          </p>
        </div>
      </section>

      {/* ───────── GRATITUDE LETTER ───────── */}
      <section className="py-20 md:py-28 relative">
        <Mandala size={420} variant="full" className="absolute -left-32 top-1/3 text-aviana-gold opacity-[0.05] pointer-events-none hidden md:block" />
        <div className="container-aviana relative max-w-3xl">
          <div className="text-center mb-12">
            <Heart size={26} className="text-aviana-gold mx-auto mb-5" />
            <p className="overline-plum mb-3">A note of deep gratitude</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink">From Holly, with love.</h2>
          </div>
          <div className="space-y-7 font-serif text-[18px] md:text-[19px] leading-[1.85] text-aviana-inkSoft">
            <p>
              When I sat down to write <em>Soaring With the Soul</em>, I imagined a woman like you holding it — curious, courageous,
              ready to look at her practice (and herself) from a new angle.
            </p>
            <p>
              I imagined you in your living room with the silks knotted to a beam, or in a sun-soaked studio, or quietly turning pages between
              poses on your mat — wondering if any of this could really be yours.
            </p>
            <p className="text-aviana-plum italic">
              It is yours. It always was. The fabric is just the doorway.
            </p>
            <p>
              This little corner of the internet exists because I wanted a way to <strong className="not-italic font-medium text-aviana-ink">reach back to you</strong> —
              a way to send you a free aerial practice, a meditation, and the simple acknowledgement that <em>I see you</em>. Amazon won't tell me your name,
              but maybe you'll tell me yours, and we can keep the conversation going.
            </p>
            <p className="font-display text-2xl md:text-3xl text-aviana-plum italic text-center pt-4">
              Thank you for finding your wings. <br className="hidden sm:block" />
              Thank you for finding me.
            </p>
            <p className="text-center text-[13px] tracking-[0.28em] uppercase text-aviana-goldDeep pt-2">— Holly Johnson</p>
          </div>
        </div>
      </section>

      {/* ───────── VIDEO PLACEHOLDER ───────── */}
      <section className="py-20 bg-aviana-cream relative overflow-hidden">
        <div className="container-aviana max-w-4xl">
          <div className="text-center mb-10">
            <p className="overline mb-4">Your gift</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink">A free aerial practice, <em className="italic gold-text">just for you.</em></h2>
            <p className="mt-5 text-aviana-muted max-w-2xl mx-auto leading-[1.8] text-[15px] md:text-[16px]">
              A short, beginner-friendly flow you can do anywhere your fabric hangs — plus a quiet, grounding meditation to close.
            </p>
          </div>

          {VIMEO_URL ? (
            <div className="relative aspect-video rounded-[2rem] overflow-hidden ring-1 ring-aviana-gold/30 shadow-2xl shadow-aviana-plumDeep/20" data-testid="soarwithme-vimeo">
              <iframe
                src={VIMEO_URL}
                title="A gift from Holly"
                className="w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="relative aspect-video rounded-[2rem] overflow-hidden bg-gradient-to-br from-aviana-plumDeep via-aviana-plum to-aviana-plumDeep ring-1 ring-aviana-gold/30 shadow-2xl shadow-aviana-plumDeep/20 flex items-center justify-center text-aviana-cream" data-testid="soarwithme-coming-soon">
              <Mandala size={520} variant="full" className="absolute text-aviana-gold opacity-[0.12] animate-spin-slow" />
              <div className="relative z-10 text-center px-8">
                <Sparkles size={32} className="mx-auto mb-5 text-aviana-goldSoft" />
                <p className="overline text-aviana-goldSoft mb-4">A work in progress</p>
                <p className="font-display text-3xl md:text-4xl mb-4">Coming soon.</p>
                <p className="font-serif italic text-[17px] md:text-[19px] text-aviana-cream/85 max-w-md mx-auto leading-[1.55]">
                  I'm preparing this practice with care. Leave your email below and I'll send it
                  the moment it's ready — straight to your inbox.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ───────── EMAIL CAPTURE ───────── */}
      <section className="py-24 md:py-32 relative">
        <Mandala size={520} variant="full" className="absolute -right-40 top-10 text-aviana-gold opacity-[0.07] pointer-events-none hidden md:block" />
        <div className="container-aviana max-w-2xl relative">
          {done ? (
            <div className="text-center py-12" data-testid="soarwithme-thankyou">
              <CheckCircle2 size={56} className="text-aviana-gold mx-auto mb-7" />
              <h2 className="font-display text-4xl md:text-5xl text-aviana-ink mb-5">
                Beautifully <em className="italic font-serif gold-text">received.</em>
              </h2>
              <p className="font-serif italic text-[19px] md:text-[21px] text-aviana-inkSoft leading-[1.6] max-w-xl mx-auto">
                Thank you for trusting me with your name, sister. The practice is coming. So is the meditation.
                In the meantime, you'll hear from me when something I think you'd love is on the horizon.
              </p>
              <p className="mt-10 text-[12px] tracking-[0.3em] uppercase text-aviana-goldDeep">
                Many blessings of love, light &amp; flight.
              </p>
            </div>
          ) : (
            <>
              <div className="text-center mb-10">
                <BookOpen size={26} className="text-aviana-gold mx-auto mb-5" />
                <p className="overline-plum mb-3">Stay close</p>
                <h2 className="font-display text-4xl md:text-5xl text-aviana-ink">
                  Tell me <em className="italic font-serif gold-text">who you are.</em>
                </h2>
                <p className="mt-5 text-aviana-muted max-w-md mx-auto leading-[1.8] text-[15px]">
                  Drop your name and email. I'll send the practice the moment it's ready and keep you in the loop
                  on anything I think you'd love — never spammy, always written by me.
                </p>
              </div>
              <form onSubmit={submit} className="space-y-4 max-w-xl mx-auto" data-testid="soarwithme-form">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                  data-testid="soarwithme-name"
                  className="w-full px-6 py-4 rounded-full bg-aviana-cream border border-aviana-border text-aviana-ink placeholder-aviana-muted/60 outline-none focus:border-aviana-gold/60 text-[15px]"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  required
                  data-testid="soarwithme-email"
                  className="w-full px-6 py-4 rounded-full bg-aviana-cream border border-aviana-border text-aviana-ink placeholder-aviana-muted/60 outline-none focus:border-aviana-gold/60 text-[15px]"
                />
                <button
                  type="submit"
                  disabled={busy}
                  className="btn-gold w-full !py-4 disabled:opacity-60"
                  data-testid="soarwithme-submit"
                >
                  <Mail size={14} /> {busy ? "Sending…" : "Send me the gift"} {!busy && <ArrowRight size={14} />}
                </button>
                <p className="text-center text-[11px] tracking-[0.18em] uppercase text-aviana-muted pt-4">
                  No spam · unsubscribe anytime · written by Holly herself
                </p>
              </form>
            </>
          )}
        </div>
      </section>

      {/* ───────── QUIET FOOTER MARK ───────── */}
      <section className="pb-14 text-center">
        <Mandala size={36} variant="lotus" className="text-aviana-gold opacity-50 mx-auto" />
      </section>
    </div>
  );
}
